import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown } from "lucide-react";
import { BUSINESS, IMAGES } from "../data/site";
import { EASE } from "./Reveal";
import { scrollTo } from "./Nav";

const FACTS = ["Freshly made food", "Quality ingredients", "Pickup available", "Local Aussie pizza shop"];

const Line = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="home" data-testid="hero-section" ref={ref} className="relative flex min-h-screen flex-col overflow-hidden bg-cream text-charcoal">
      <motion.div style={{ y: imgY }} className="absolute inset-y-[-10%] right-0 w-full md:w-[58%]">
        <img
          src={IMAGES.heroPizza}
          alt="Fresh pizza at Croudace Pizza & Burger"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-cream via-cream/55 to-cream/5" />
        <div className="absolute inset-0 bg-gradient-to-t from-cream via-cream/20 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-5 pt-36 pb-16 md:px-10">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          className="mb-6 text-xs font-semibold uppercase tracking-[0.4em] text-ember"
          data-testid="hero-kicker"
        >
          New Lambton Heights · Pizza &amp; Burger
        </motion.p>
        <h1 className="font-serif leading-[1.02] tracking-tight text-[13vw] sm:text-[10vw] lg:text-[7.5rem]">
          <Line delay={0.3}>Roman Style</Line>
          <Line delay={0.45} className="italic text-ember">Pizza.</Line>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
          className="mt-8 max-w-md text-base text-charcoal/65 md:text-lg"
          data-testid="hero-subheading"
        >
          Delicious pizzas, burgers and more, made fresh for you at Croudace Pizza &amp; Burger.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            data-testid="hero-order-button"
            href={BUSINESS.phoneHref}
            className="btn-pulse group flex items-center gap-3 rounded-full bg-ember px-9 py-4 text-sm font-bold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-105 hover:bg-[#991B1B]"
          >
            <Phone size={16} className="transition-transform duration-300 group-hover:rotate-12" />
            Call to Order
          </a>
          <button
            data-testid="hero-menu-button"
            onClick={() => scrollTo("#menu")}
            className="rounded-full bg-charcoal px-9 py-4 text-sm font-bold uppercase tracking-widest text-cream transition-all duration-300 hover:scale-105 hover:bg-ember"
          >
            View Menu
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="relative z-10 border-t border-black/10"
      >
        <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-black/10 md:grid-cols-4">
          {FACTS.map((f, i) => (
            <div key={f} data-testid={`hero-fact-${i}`} className="flex items-center px-4 py-5 md:px-6">
              <span className="text-xs font-medium uppercase tracking-widest text-charcoal/60">{f}</span>
            </div>
          ))}
        </div>
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="pointer-events-none absolute -top-16 right-6 hidden md:block"
        >
          <ArrowDown className="text-charcoal/40" size={20} />
        </motion.div>
      </motion.div>
    </section>
  );
}
